import React, { useState, useEffect } from "react";

/**
 * -----------------------------------------Event Management page----------------------------------
 */
import "../../assets/event_management.css";
import { useEvent } from "../../lib/hooks/useEvent";
import {
  startEvent,
  cancelEvent,
  endEvent,
} from "../../lib/services/eventService";
import Navbar from "../components/Navbar/Navbar.jsx";
import { adminStatus } from "../../lib/services/authService.js";
import { resetStencilWeekStore } from "../../lib/stores/index.js";
import { useStencils } from "../../lib/hooks/useStencils.js";
import { getTotalVolunteerTimes } from "../../lib/utils/time.js";

/* Centered modal with backdrop */
const ConfirmPanel = ({ kind, onConfirm, onClose }) => {
  const isEnd = kind === "end";
  const verb = isEnd ? "end" : "cancel";

  // Close with ESC key
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="confirm-overlay" role="presentation" onClick={onClose}>
      {/* stop propagation so clicks inside the card don't close */}
      <div
        className="confirm-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="confirm-text">
          Are you sure you want to{" "}
          <span className={isEnd ? "confirm-verb-end" : "confirm-verb-cancel"}>
            {verb}
          </span>{" "}
          the event?
        </p>

        <div className="confirm-actions">
          <button className="btn confirm-no" onClick={onClose}>
            No
          </button>
          <button className="btn confirm-yes" onClick={onConfirm}>
            Yes
          </button>
        </div>
      </div>
    </div>
  );
};

const EventManagement = ({ isAdminAuthenticated, setIsAdminAuthenticated }) => {
  const [eventData, setEventData] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null); // 'end' | 'cancel' | null
  const { event, setEvent, getEvent } = useEvent();
  const { stencils, getStencils } = useStencils();
  const { clearSelectedStatuses } = useStencils();

  const [traceTime, setTraceTime] = useState(0);
  const [carveTime, setCarveTime] = useState(0);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const response = await adminStatus();
      if (response.ok) {
        const data = await response.json();
        setIsAdminAuthenticated(data.isAuthenticated);
      }
    };

    checkAdminStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const weeks = document.querySelector("input[name='weeks']:checked")?.value;
    const year = document.getElementById("year").value.trim();
    //const count = document.getElementById("stencilCount").value.trim();
    if (!weeks || !year) return;

    const response = await startEvent(weeks, year);
    if (response.ok) {
      const event = await response.json();
      setEvent({
        isActive: Number(event.data.isActive),
        num_weeks: Number(event.data.num_weeks),
        year: event.data.year,
        volunteer_code: event.data.volunteer_code,
      });
    } else {
      alert("Error starting event");
    }

    setEventData({ weeks: Number(weeks), year: Number(year) });
  };

  useEffect(() => {
    if (!event || Object.keys(event).length === 0) getEvent();
    if (!stencils || stencils.length === 0) getStencils();
  }, []);

  useEffect(() => {
    const { trace, carve } = getTotalVolunteerTimes(stencils, event.num_weeks);
    setTraceTime(trace);
    setCarveTime(carve);
  }, [stencils, event]);

  const isEventActive =
    event && Object.keys(event).length > 0 && event.isActive === 1;

  const cancelOrEnd = async () => {
    if (confirmKind === "cancel") await cancel();
    else if (confirmKind === "end") await end();
  };

  function clearStencilStatuses() {
    clearSelectedStatuses();
    resetStencilWeekStore();
  }

  const end = async () => {
    const response = await endEvent();
    if (response.ok) {
      const event = await response.json();
      setEvent({
        isActive: Number(event.data.isActive),
        num_weeks: Number(event.data.num_weeks),
        year: event.data.year,
        volunteer_code: event.data.volunteer_code,
      });
      getStencils();
    } else {
      alert("Error cancelling event");
    }

    // After confirming, reset back to create screen
    setEventData(null);
    setConfirmKind(null);
  };

  // Cancel the event
  const cancel = async () => {
    // Call to cancel event and then update local stores on success
    const response = await cancelEvent();
    if (response.ok) {
      const event = await response.json();
      setEvent({
        isActive: Number(event.data.isActive),
        num_weeks: Number(event.data.num_weeks),
        year: event.data.year,
        volunteer_code: event.data.volunteer_code,
      });
      clearStencilStatuses();
    } else {
      alert("Error cancelling event");
    }

    // After confirming, reset back to create screen
    setEventData(null);
    setConfirmKind(null);
  };

  return (
    <div>
      <Navbar
        isAdminAuthenticated={isAdminAuthenticated}
        setIsAdminAuthenticated={setIsAdminAuthenticated}
        location={"/admin/event-management"}
      />
      <div className="main-frame" style={{ paddingTop: "100px" }}>
        {/* CREATE MODE */}
        {!isEventActive && (
          <div className="text-center">
            <h2 className="text-center mb-4">Create New Event</h2>
            <div className="event-card">
              <form id="createEventForm" onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="d-block mb-2">
                    Select Number of Weeks:
                  </label>

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="weeks"
                      id="weeks1"
                      value="1"
                      required
                    />
                    <label className="form-check-label" htmlFor="weeks1">
                      1
                    </label>
                  </div>

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="weeks"
                      id="weeks2"
                      value="2"
                    />
                    <label className="form-check-label" htmlFor="weeks2">
                      2
                    </label>
                  </div>

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="weeks"
                      id="weeks3"
                      value="3"
                    />
                    <label className="form-check-label" htmlFor="weeks3">
                      3
                    </label>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="year" className="form-label">
                    Enter Event Year:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="year"
                    name="year"
                    placeholder="e.g. 2025"
                    min="2020"
                    required
                  />
                </div>

                {/* <div className="mb-4">
                <label htmlFor="stencilCount" className="form-label">Enter Stencil Count:</label>
                <input
                  type="number"
                  className="form-control"
                  id="stencilCount"
                  name="stencilCount"
                  min="1"
                  required
                />
              </div> */}

                <div className="text-center">
                  <button type="submit" className="btn btn-start">
                    Start Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* CURRENT EVENT MODE */}
        {!!isEventActive && (
          <div className="current-event-wrap">
            <h2 className="text-center mb-4">Current Event</h2>

            <div className="event-card current-event-card">
              {/* Weeks (disabled radios to mirror mock) */}
              <div className="mb-3">
                <label className="d-block mb-2">Number of Weeks</label>
                {[1, 2, 3].map((w) => (
                  <div key={w} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      disabled
                      checked={event.num_weeks === w}
                      readOnly
                    />
                    <label className="form-check-label">{w}</label>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <label className="form-label">Event Year</label>
                <input
                  className="form-control"
                  value={event.year}
                  disabled
                  readOnly
                />
              </div>

              <hr />

              <div className="mb-3 text-center">
                <h6>
                  Volunteer Access Code: <b>{`${event.volunteer_code}`}</b>
                </h6>
                <h6>
                  Expected Tracing Time: <b>{`${traceTime}`} hours</b>
                </h6>
                <h6>
                  Expected carving Time: <b>{`${carveTime}`} hours</b>
                </h6>
              </div>
            </div>

            <div className="action-row">
              <button
                className="btn btn-end me-3"
                onClick={() => setConfirmKind("end")}
              >
                End Event
              </button>
              <button
                className="btn btn-cancel"
                onClick={() => setConfirmKind("cancel")}
              >
                Cancel Event
              </button>
            </div>

            {/* Centered confirm modal */}
            {confirmKind && (
              <ConfirmPanel
                kind={confirmKind}
                onConfirm={cancelOrEnd}
                onClose={() => setConfirmKind(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManagement;
